import { useState } from 'react'
import { Navigate, NavLink, Route, Routes, useParams } from 'react-router-dom'
import { PenLine } from 'lucide-react'
import { AppName } from '@/lib/AppName'
import { useRedeemApp, useRedeemApps } from '@/lib/queries'
import { appLabel } from '@/lib/redeem'
import { cn } from '@/lib/utils'
import { Button, Dialog, ErrorState, PageHeader, Skeleton, TonePill } from '@/ui'
import { DeskPostForm } from '../schedule/DeskPostForm'
import AppCodes from './AppCodes'
import AppOverview from './AppOverview'
import AppPosts from './AppPosts'
import AppRequests from './AppRequests'
import AppSettings from './AppSettings'
import { APP_PLATFORM, AppStatusSelect, AutoSendSwitch, Expiry } from './shared'

const TABS = [
  { path: '', label: '개요' },
  { path: '/codes', label: '코드' },
  { path: '/requests', label: '요청' },
  { path: '/posts', label: '글' },
  { path: '/settings', label: '설정' },
]

/** /redeem/:appId/*: one app's pool, requests, posts and settings. */
export default function AppWorkspace() {
  const id = Number(useParams().appId)
  const q = useRedeemApp(id)
  const allApps = (useRedeemApps().data?.items ?? []).map((i) => i.app)
  const [writing, setWriting] = useState(false)
  if (!Number.isInteger(id) || id <= 0) return <Navigate to="/redeem" replace />

  const detail = q.data
  const base = `/redeem/${id}`

  if (!detail) {
    return (
      <>
        <PageHeader title="리딤" parent={{ to: '/redeem', label: '리딤' }} />
        {q.error ? <ErrorState title="앱을 불러오지 못했다" error={q.error} onRetry={() => q.refetch()} /> : <Skeleton className="h-48" />}
      </>
    )
  }
  const { app } = detail

  return (
    <>
      <PageHeader
        parent={{ to: '/redeem', label: '리딤' }}
        title={
          <>
            <AppName app={app} apps={allApps} />
            {app.ended_at && <TonePill tone="neutral" label="나눔 종료" className="ml-2 align-middle" />}
          </>
        }
        description={
          <span className="text-table">
            {app.slug} · {APP_PLATFORM[app.platform]} · {app.product_name || '해제되는 것 미지정'}
            {app.expires_on && (
              <>
                {' · '}코드 만료 <Expiry date={app.expires_on} />
              </>
            )}
          </span>
        }
        actions={
          <>
            <AppStatusSelect app={app} className="h-9" />
            <AutoSendSwitch app={app} />
            <Button variant="primary" icon={PenLine} onClick={() => setWriting(true)}>
              데스크 글 쓰기
            </Button>
          </>
        }
      >
        <nav aria-label={`${appLabel(app, allApps)} 화면`} className="-mx-1 flex overflow-x-auto border-b border-border px-1">
          {TABS.map((t) => (
            <NavLink
              key={t.path}
              to={`${base}${t.path}`}
              end
              className={({ isActive }) =>
                cn(
                  'relative shrink-0 whitespace-nowrap px-3 py-2 text-body font-medium transition-colors',
                  isActive
                    ? 'text-foreground after:absolute after:inset-x-3 after:-bottom-px after:h-0.5 after:bg-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )
              }
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
      </PageHeader>

      <Routes>
        <Route index element={<AppOverview detail={detail} />} />
        <Route path="codes" element={<AppCodes detail={detail} />} />
        <Route path="requests" element={<AppRequests appId={id} />} />
        <Route path="posts" element={<AppPosts app={app} />} />
        <Route path="settings" element={<AppSettings app={app} />} />
        <Route path="*" element={<Navigate to={base} replace />} />
      </Routes>

      <Dialog open={writing} onClose={() => setWriting(false)} title={`${appLabel(app, allApps)} 데스크 글 쓰기`} size="lg">
        <div className="max-h-[70vh] overflow-y-auto pb-2">
          <DeskPostForm initialAppId={id} onDone={() => setWriting(false)} />
        </div>
      </Dialog>
    </>
  )
}
